import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _values from "lodash/values";
import _get from "lodash/get";
import React, { Fragment, useContext } from 'react';
import { colors } from '@nerdwallet/base-styles/variables';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import { TrkElement } from '@nerdwallet/nw-client-lib/trk-helper';
import MarketplaceApplyNow, { APPLY_NOW_LINK_TYPES, LINK_TYPES } from '@nerdwallet/react-marketplace-apply-now';
import { ImpressionContext } from '@nerdwallet/analytics/react/impression';
import ProductCardBanner, { ProductCardBannerTypes } from '@nerdwallet/react-product-card-banner';
import BaseMarketplaceProductCardScaffold from './base-marketplace-product-card-scaffold';
import Content from './content';
import Drivers from './drivers';
import Header from './header';
import MatchScoreCTA from './match-score-cta/match-score-cta';
import Preheader from './preheader';
import ProductCardImageWrapper from './product-card-image-wrapper';
import ProductCtas from './product-ctas';
import mapMarketplaceEntityToProps, { DRIVER_GROUPS } from './mappings';
import { KetoProductCard } from './keto-product-card/keto-product-card';
import { buildTrkSectionCategory, getMonetizable } from './lib/utils';
import { CONTENT_TABS, PREHEADER_TYPE, SENTIMENT_TYPE, TRK_PRODUCT_LOCATION, COMPONENT_ANALYTICS_NAME } from './lib/constants';
import styles from "./marketplace-product-card.module.css";

// Make DRIVER_GROUPS available to implementations directly.
export { DRIVER_GROUPS };
export { CONTENT_TABS, PREHEADER_TYPE, SENTIMENT_TYPE };
export { KetoProductCard }; // exporting KetoProductCard

export var BaseMarketplaceProductCard = function BaseMarketplaceProductCard(_ref) {
  var _analytics$productCta, _analytics$contentPro;
  var activeContentTab = _ref.activeContentTab,
    analytics = _ref.analytics,
    bottomLine = _ref.bottomLine,
    className = _ref.className,
    cons = _ref.cons,
    ctaHref = _ref.ctaHref,
    ctaShowIcon = _ref.ctaShowIcon,
    ctaSubtext = _ref.ctaSubtext,
    ctaText = _ref.ctaText,
    drivers = _ref.drivers,
    enableAnalytics = _ref.enableAnalytics,
    hasAccordionOnMobile = _ref.hasAccordionOnMobile,
    heading = _ref.heading,
    imageData = _ref.imageData,
    initTabContentExpanded = _ref.initTabContentExpanded,
    keyFacts = _ref.keyFacts,
    marketplaceEntity = _ref.marketplaceEntity,
    marketplaceEntitySlug = _ref.marketplaceEntitySlug,
    omitCta = _ref.omitCta,
    omitRating = _ref.omitRating,
    omitRatingTextHref = _ref.omitRatingTextHref,
    omitTabs = _ref.omitTabs,
    omittedTabs = _ref.omittedTabs,
    parentImpressionId = _ref.parentImpressionId,
    productDetails = _ref.productDetails,
    productType = _ref.productType,
    pros = _ref.pros,
    reviewLinkHref = _ref.reviewLinkHref,
    reviewLinkTabIndex = _ref.reviewLinkTabIndex,
    secondaryCtaHref = _ref.secondaryCtaHref,
    secondaryCtaText = _ref.secondaryCtaText,
    shouldLogImpression = _ref.shouldLogImpression,
    starRating = _ref.starRating,
    taxonomy = _ref.taxonomy,
    trkPageType = _ref.trkPageType,
    trkProductPosition = _ref.trkProductPosition,
    trkSectionCategory = _ref.trkSectionCategory,
    wrapDrivers = _ref.wrapDrivers,
    _handleCcCompareClick = _ref._handleCcCompareClick,
    _hasCcCompareButton = _ref._hasCcCompareButton,
    hasPreheader = _ref.hasPreheader,
    identity = _ref.identity,
    preheaderHoverText = _ref.preheaderHoverText,
    preheaderSentiment = _ref.preheaderSentiment,
    preheaderText = _ref.preheaderText,
    preheaderType = _ref.preheaderType,
    preheaderUrl = _ref.preheaderUrl,
    showMatchScoreCTA = _ref.showMatchScoreCTA,
    _ccCompareButtonTextBeforeClick = _ref._ccCompareButtonTextBeforeClick,
    _ccCompareButtonTextAfterClick = _ref._ccCompareButtonTextAfterClick,
    lazyLoadEnabled = _ref.lazyLoadEnabled,
    displayPage = _ref.displayPage,
    isKetoCard = _ref.isKetoCard,
    shouldDisplayPlaceHolder = _ref.shouldDisplayPlaceHolder,
    hasBanner = _ref.hasBanner,
    bannerText = _ref.bannerText,
    bannerType = _ref.bannerType;
  var impressionContext = useContext(ImpressionContext);
  var productCardImage = /*#__PURE__*/React.createElement(ProductCardImageWrapper, {
    imageData: imageData,
    backgroundColor: colors.transparent,
    imageClassName: styles.productImage,
    className: styles.productImageWrapper,
    objectFit: "contain",
    lazyLoadEnabled: lazyLoadEnabled,
    isKetoCard: false,
    shouldDisplayPlaceHolder: shouldDisplayPlaceHolder
  });
  var mpcTrkSectionCategory = buildTrkSectionCategory(trkSectionCategory, TRK_PRODUCT_LOCATION);
  var getProductCtas = function getProductCtas(ctaClassname, ctaLocation) {
    return !omitCta ? /*#__PURE__*/React.createElement(ProductCtas, {
      analytics: analytics === null || analytics === void 0 ? void 0 : analytics.productCtaProps,
      className: ctaClassname,
      ctaHref: ctaHref,
      ctaShowIcon: ctaShowIcon,
      ctaSubtext: ctaSubtext,
      ctaText: ctaText,
      enableAnalytics: enableAnalytics,
      id: "".concat(TRK_PRODUCT_LOCATION, "__product-button--").concat(ctaLocation, "--").concat(trkProductPosition),
      marketplaceEntity: marketplaceEntity,
      reviewLinkHref: reviewLinkHref,
      secondaryCtaHref: secondaryCtaHref,
      secondaryCtaText: secondaryCtaText,
      shouldLogImpression: shouldLogImpression,
      taxonomy: taxonomy,
      trkPageType: trkPageType,
      trkProductPosition: trkProductPosition,
      trkSectionCategory: mpcTrkSectionCategory
      // BNK specific props
      ,
      displayPage: displayPage
      // CC specific props
      ,
      _hasCcCompareButton: _hasCcCompareButton,
      _handleCcCompareClick: _handleCcCompareClick,
      _ccCompareButtonTextBeforeClick: _ccCompareButtonTextBeforeClick,
      _ccCompareButtonTextAfterClick: _ccCompareButtonTextAfterClick
    }) : null;
  };
  var productCardPreheader = hasPreheader ? /*#__PURE__*/React.createElement(Preheader, {
    className: styles.preheader,
    taxonomy: taxonomy,
    text: preheaderText,
    title: preheaderHoverText,
    trkPageType: trkPageType,
    sentiment: preheaderSentiment,
    type: preheaderType,
    url: preheaderUrl
  }) : null;
  var productCardBanner = hasBanner ? /*#__PURE__*/React.createElement(ProductCardBanner, {
    type: bannerType,
    text: bannerText
  }) : null;
  var matchScoreCTA = showMatchScoreCTA ? /*#__PURE__*/React.createElement(MatchScoreCTA, {
    analytics: analytics === null || analytics === void 0 ? void 0 : analytics.matchScoreCTAProps,
    identity: identity,
    marketplaceEntity: marketplaceEntity,
    taxonomy: taxonomy
  }) : null;
  var driversAndContent = /*#__PURE__*/React.createElement(Fragment, null, !!drivers && /*#__PURE__*/React.createElement(Drivers, {
    analytics: analytics === null || analytics === void 0 ? void 0 : analytics.driversProps,
    className: styles.drivers,
    drivers: drivers,
    enableAnalytics: enableAnalytics,
    wrapDrivers: wrapDrivers
  }), !omitTabs && !isKetoCard && /*#__PURE__*/React.createElement(Content, {
    activeContentTab: activeContentTab,
    bottomLine: bottomLine,
    cons: cons,
    analytics: analytics === null || analytics === void 0 ? void 0 : analytics.contentProps,
    enableAnalytics: enableAnalytics,
    id: marketplaceEntitySlug,
    initTabContentExpanded: initTabContentExpanded,
    keyFacts: keyFacts,
    marketplaceEntity: marketplaceEntity,
    omitExpandFunctionalityOnMobile: hasAccordionOnMobile,
    omittedTabs: omittedTabs,
    parentImpressionId: parentImpressionId,
    productDetails: productDetails,
    pros: pros,
    reviewLinkHref: reviewLinkHref,
    reviewLinkTabIndex: reviewLinkTabIndex,
    taxonomy: taxonomy,
    trkPageType: trkPageType,
    trkSectionCategory: mpcTrkSectionCategory,
    trkSectionPosition: trkProductPosition
  }));
  var header = /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Header, {
    analytics: analytics === null || analytics === void 0 ? void 0 : analytics.headerProps,
    className: styles.header,
    ctaHref: ctaHref,
    enableAnalytics: enableAnalytics,
    heading: heading,
    marketplaceEntity: marketplaceEntity,
    productType: productType,
    omitRating: omitRating,
    omitRatingTextHref: omitRatingTextHref,
    starRating: starRating,
    reviewLinkHref: reviewLinkHref,
    omitCta: omitCta,
    shouldLogImpression: shouldLogImpression,
    taxonomy: taxonomy,
    trkPageType: trkPageType,
    trkProductPosition: trkProductPosition,
    trkSectionCategory: mpcTrkSectionCategory
    // BNK specific props
    ,
    displayPage: displayPage
  }), getProductCtas(styles['productCtas--underHeader'], 'under-header'));
  var imageAndCtas = /*#__PURE__*/React.createElement(Fragment, null, imageData !== null && imageData !== void 0 && imageData.src ? omitCta ? productCardImage : /*#__PURE__*/React.createElement(MarketplaceApplyNow, {
    id: "".concat(TRK_PRODUCT_LOCATION, "__product-image--").concat(trkProductPosition),
    impressionId: impressionContext === null || impressionContext === void 0 ? void 0 : impressionContext.impressionId,
    link: ctaHref,
    linkType: APPLY_NOW_LINK_TYPES.productImage,
    marketplaceEntity: marketplaceEntity,
    monetizable: getMonetizable(marketplaceEntity),
    productLocation: TRK_PRODUCT_LOCATION,
    productPosition: trkProductPosition,
    reviewLink: reviewLinkHref,
    sectionName: (analytics === null || analytics === void 0 ? void 0 : (_analytics$productCta = analytics.productCtaProps) === null || _analytics$productCta === void 0 ? void 0 : _analytics$productCta.sectionName) || COMPONENT_ANALYTICS_NAME,
    shouldLogImpression: shouldLogImpression,
    trkElement: TrkElement.image,
    trkPageType: trkPageType,
    trkSectionCategory: buildTrkSectionCategory(mpcTrkSectionCategory, 'productImage'),
    trkTaxonomy: taxonomy,
    type: LINK_TYPES.LINK
    // BNK specific props
    ,
    displayPage: displayPage,
    disableBankCtaAnnotation: true
  }, productCardImage) : null, getProductCtas(styles['productCtas--underImage'], 'under-image'));
  return /*#__PURE__*/React.createElement(BaseMarketplaceProductCardScaffold, {
    analytics: analytics === null || analytics === void 0 ? void 0 : (_analytics$contentPro = analytics.contentProps) === null || _analytics$contentPro === void 0 ? void 0 : _analytics$contentPro.expandableContentProps,
    className: className,
    driversAndContentNode: driversAndContent,
    hasAccordionOnMobile: hasAccordionOnMobile,
    hasPreheaderBadge: preheaderType === PREHEADER_TYPE.BADGE,
    headerNode: header,
    imageNode: imageAndCtas,
    matchScoreCtaNode: matchScoreCTA,
    preheaderNode: productCardPreheader,
    bannerNode: productCardBanner
  });
};
BaseMarketplaceProductCard.defaultProps = {
  displayPage: '',
  enableAnalytics: false,
  hasAccordionOnMobile: false,
  hasPreheader: false,
  preheaderType: PREHEADER_TYPE.PLAIN_TEXT,
  taxonomy: _get(global.window, 'nwPageTaxonomy'),
  // CC specific props
  identity: {},
  showMatchScoreCTA: false,
  // Keto Card specific props
  isKetoCard: false
};
var MarketplaceProductCard = function MarketplaceProductCard(props) {
  useDeprecatedPropsWarning({
    component: 'MarketplaceProductCard',
    deprecatedProps: [{
      version: '6.9.0',
      deprecated: 'enableAnalytics',
      migration: 'Use analytics instead.'
    }, {
      version: '6.9.0',
      deprecated: 'parentImpressionId',
      migration: 'No longer needed. React context used instead or use the MarketplaceProductCardImpression component instead.'
    }],
    props: props
  });
  if (!props.marketplaceEntity) {
    return null;
  }
  var mappedProps = mapMarketplaceEntityToProps(props);
  return /*#__PURE__*/React.createElement(BaseMarketplaceProductCard, mappedProps);
};
MarketplaceProductCard.defaultProps = {
  driverGroup: DRIVER_GROUPS.DEFAULT_DRIVER_GROUP,
  marketplaceEntitiesForCdApyTerms: {},
  wrapDrivers: false,
  // CC specific props
  hidePrequalCta: false,
  // Keto Card specific props
  isKetoCard: false,
  // bedrock props
  analytics: {
    enabled: false
  },
  lazyLoadEnabled: false,
  approvalOddsEnabled: false
};
if (process.env.NODE_ENV !== 'production') {
  /* eslint-disable global-require, react/forbid-prop-types */
  var PropTypes = require('prop-types');
  BaseMarketplaceProductCard.propTypes = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, Content.propTypes), Header.propTypes), Drivers.propTypes), ProductCtas.propTypes), {}, {
    className: PropTypes.string,
    ctaHref: PropTypes.string,
    ctaShowIcon: PropTypes.bool,
    ctaSubtext: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    ctaText: PropTypes.string,
    displayPage: PropTypes.string,
    enableAnalytics: PropTypes.bool,
    hasAccordionOnMobile: PropTypes.bool,
    imageData: PropTypes.object,
    marketplaceEntity: PropTypes.object,
    marketplaceEntitySlug: PropTypes.string,
    omitCta: PropTypes.bool,
    omitRating: PropTypes.bool,
    omitRatingTextHref: PropTypes.bool,
    omitTabs: PropTypes.bool,
    omittedTabs: PropTypes.arrayOf(PropTypes.oneOf(_values(CONTENT_TABS))),
    productType: PropTypes.string,
    shouldLogImpression: PropTypes.bool,
    taxonomy: PropTypes.object,
    trkSectionCategory: PropTypes.string,
    // CC specific props
    identity: PropTypes.object,
    hasPreheader: PropTypes.bool,
    matchScore: PropTypes.number,
    // eslint-disable-line react/no-unused-prop-types
    preheaderHoverText: PropTypes.string,
    preheaderSentiment: PropTypes.oneOf(_values(SENTIMENT_TYPE)),
    preheaderText: PropTypes.string,
    preheaderType: PropTypes.oneOf(_values(PREHEADER_TYPE)),
    preheaderUrl: PropTypes.string,
    showMatchScoreCTA: PropTypes.bool,
    // MORTG specific props
    minCreditScoreType: PropTypes.string,
    // eslint-disable-line react/no-unused-prop-types

    // Keto Card specific props
    isKetoCard: PropTypes.bool,
    hasBanner: PropTypes.bool,
    bannerText: PropTypes.string,
    bannerType: PropTypes.oneOf(_values(ProductCardBannerTypes))
  });
  MarketplaceProductCard.propTypes = {
    driverGroup: PropTypes.oneOf(_values(DRIVER_GROUPS)),
    marketplaceEntity: PropTypes.object,
    marketplaceEntitiesForCdApyTerms: PropTypes.shape({
      oneYear: PropTypes.object,
      threeYear: PropTypes.object,
      fiveYear: PropTypes.object
    }),
    wrapDrivers: PropTypes.bool,
    hidePrequalCta: PropTypes.bool,
    // Keto Card specific props
    isKetoCard: PropTypes.bool,
    // bedrock props
    /** @deprecated A flag to enable analytics */
    enableAnalytics: PropTypes.bool,
    /** @deprecated Impression id of the wrapping impression */
    parentImpressionId: PropTypes.string,
    analytics: PropTypes.shape({
      contentProps: Content.propTypes.analytics,
      driversProps: Drivers.propTypes.analytics,
      headerProps: Header.propTypes.analytics,
      loaCtaProps: PropTypes.shape({
        sectionName: PropTypes.string,
        sectionPosition: PropTypes.number
      }),
      matchScoreCTAProps: MatchScoreCTA.propTypes.analytics,
      productCtaProps: PropTypes.shape({
        sectionName: PropTypes.string,
        // Props passed down from the product impression used for Rates & Fees links
        payload: PropTypes.shape({
          element_position: PropTypes.number,
          section_name: PropTypes.string,
          section_position: PropTypes.number
        })
      }),
      /** @deprecated should be using the above props */
      enabled: PropTypes.bool,
      payload: PropTypes.shape({
        elementName: PropTypes.string,
        parentImpressionId: PropTypes.string
      }),
      prequalCTATrk: PropTypes.string
    }),
    lazyLoadEnabled: PropTypes.bool,
    approvalOddsEnabled: PropTypes.bool,
    shouldDisplayPlaceHolder: PropTypes.bool,
    hasBanner: PropTypes.bool,
    bannerText: PropTypes.string,
    bannerType: PropTypes.oneOf(_values(ProductCardBannerTypes))
  };
  /* eslint-enable global-require, react/forbid-prop-types */
}

export default MarketplaceProductCard;